<template>
  <v-container>
    <!-- Filter Chips -->
    <v-row class="mb-4">
      <v-chip-group v-model="selectedType" mandatory active-class="primary--text" column>
        <!-- "All" chip with total image count -->
        <v-chip value="">
          All ({{ allImages.length }})
        </v-chip>
        <!-- Chips for each image type (only shown if count > 0) -->
        <v-chip v-for="chip in availableChips" :key="chip.type" :value="chip.type">
          {{ chip.type }} ({{ chip.count }})
        </v-chip>
      </v-chip-group>
    </v-row>

    <!-- Image Grid -->
    <v-row>
      <v-col v-for="image in filteredImages" :key="image.src">
        <v-card class="mx-auto" max-width="200">
          <div class="image-container">
            <v-img
              class="align-end text-white"
              :src="activeProvider === provider.KBS ? getPaddedBackgroundImageUrl_200x200(image.src) : image.src"
              height="200"
              cover
            >
              <v-card-title>
                <v-chip color="primary" variant="elevated" size="small" density="comfortable">
                  {{ image.type }}
                </v-chip>
              </v-card-title>
            </v-img>
            <v-btn icon class="open-icon" @click="openImageUrl(image.parentPathname)">
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
          <v-card-subtitle :title="image.alt" class="pt-4">
            {{ image.alt }}
          </v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { usePagesStore } from '@/store/pagesStore';
import { useProductsStore } from '@/store/productsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { useActiveUrlStore } from '@/store/activeUrlStore';
import provider from '@/enums/Provider';
import { getPaddedBackgroundImageUrl_200x200 } from '@/helpers/getPaddedBackgroundImageUrl';

// Access the stores
const pagesStore = usePagesStore();
const productsStore = useProductsStore();
const categoriesStore = useCategoriesStore();
const activeUrlStore = useActiveUrlStore();

const activeProvider = activeUrlStore.getProvider;

// Combine images from all stores
const allImages = computed(() => {
  const productImages = productsStore.getAllImages;
  const pageImages = pagesStore.getAllImages;
  const categoryImages = categoriesStore.getAllImages;
  return [...productImages, ...pageImages, ...categoryImages];
});

// Merge image type counts from all stores
const imageTypeCounts = computed(() => {
  const counts: Record<string, number> = {};

  const mergeCounts = (storeCounts: Record<string, number>) => {
    Object.keys(storeCounts).forEach(type => {
      counts[type] = (counts[type] || 0) + storeCounts[type];
    });
  };

  mergeCounts(productsStore.getImageTypeCounts);
  mergeCounts(categoriesStore.getImageTypeCounts);
  mergeCounts(pagesStore.getImageTypeCounts);

  return counts;
});

// Build a chips array from the counts (only include types with a count > 0)
const availableChips = computed(() => {
  return Object.keys(imageTypeCounts.value)
    .filter(type => imageTypeCounts.value[type] > 0)
    .map(type => ({
      type,
      count: imageTypeCounts.value[type]
    }));
});

// Selected type for filtering (empty string means "All")
const selectedType = ref<string>("");

// Compute filtered images based on the selected type
const filteredImages = computed(() => {
  if (selectedType.value === "") {
    return allImages.value;
  }
  return allImages.value.filter(image => image.type === selectedType.value);
});

// Function to open image URL in a new tab using the store's function
const openImageUrl = (pathname: string) => {
  const url = activeUrlStore.getUrlByPathname(pathname);
  window.open(url, '_blank');
};
</script>

<style scoped>
.image-container {
  position: relative;
}
.open-icon {
  position: absolute;
  top: 8px;
  right: 8px;
}

</style>
